/*
 * Smores side cart controller.
 * - Intercepts every /cart/add form submit -> AJAX add -> opens the drawer.
 * - Qty +/-, remove, and upsell "Add" use /cart/change.js & /cart/add.js.
 * - Refreshes the drawer in place via the Section Rendering API (sections=
 *   smores-cart-drawer), so the free-shipping bar, items, upsells and
 *   subtotal always reflect the live cart. Open state lives on <html>.
 * - Header cart icon opens the drawer instead of navigating to /cart.
 * - Esc / overlay / close button close it.
 * Other scripts can fire `document.dispatchEvent(new Event('smores:cart:refresh'))`
 * after a custom add (e.g. the "Add all to cart" bundle) to refresh + open.
 */
(function () {
  const SECTION_ID = 'smores-cart-drawer';
  const root = document.documentElement;
  let lastFocus = null;

  const drawer = () => document.querySelector('[data-smores-cart]');

  function open() {
    if (!drawer()) return;
    lastFocus = document.activeElement;
    root.classList.add('smores-cart-open');
    drawer().setAttribute('aria-hidden', 'false');
    const closeBtn = document.querySelector('.smores-cart__close');
    if (closeBtn) closeBtn.focus({ preventScroll: true });
  }
  function close() {
    root.classList.remove('smores-cart-open');
    if (drawer()) drawer().setAttribute('aria-hidden', 'true');
    if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true });
  }
  function setBusy(b) { if (drawer()) drawer().setAttribute('aria-busy', b ? 'true' : 'false'); }

  function applySection(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const freshPanel = doc.querySelector('.smores-cart__panel');
    const livePanel = document.querySelector('.smores-cart__panel');
    if (freshPanel && livePanel) livePanel.innerHTML = freshPanel.innerHTML;
    const freshRoot = doc.querySelector('[data-smores-cart]');
    if (freshRoot && drawer()) {
      const count = freshRoot.dataset.itemCount;
      drawer().dataset.itemCount = count;
      updateBadge(count);
    }
  }

  function updateBadge(count) {
    const cartBtn = document.querySelector('.smores-header__icon-btn--cart');
    if (!cartBtn) return;
    const n = parseInt(count, 10) || 0;
    let badge = cartBtn.querySelector('.smores-header__cart-count');
    const prev = badge ? (parseInt(badge.textContent, 10) || 0) : 0;
    if (n > prev) {
      cartBtn.classList.remove('is-bumped');
      void cartBtn.offsetWidth; /* restart animation */
      cartBtn.classList.add('is-bumped');
      setTimeout(() => cartBtn.classList.remove('is-bumped'), 600);
    }
    if (n > 0) {
      if (!badge) {
        badge = document.createElement('span');
        badge.className = 'smores-header__cart-count';
        badge.setAttribute('aria-hidden', 'true');
        cartBtn.appendChild(badge);
      }
      badge.textContent = n;
    } else if (badge) {
      badge.remove();
    }
    cartBtn.setAttribute('aria-label', 'Cart, ' + n + ' items');
  }

  async function refresh() {
    try {
      const res = await fetch(location.pathname + '?section_id=' + SECTION_ID, { headers: { 'Accept': 'text/html' } });
      applySection(await res.text());
    } catch (e) { /* noop */ }
  }

  async function postCart(url, body) {
    setBusy(true);
    try {
      const isForm = body instanceof FormData;
      const res = await fetch(url, {
        method: 'POST',
        headers: isForm ? { 'Accept': 'application/json' } : { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: isForm ? body : JSON.stringify(body),
      });
      const data = await res.json().catch(() => null);
      if (data && data.sections && data.sections[SECTION_ID]) applySection(data.sections[SECTION_ID]);
      else await refresh();
      return data;
    } finally { setBusy(false); }
  }

  const addVariant = (variantId, qty) =>
    postCart('/cart/add.js', { items: [{ id: Number(variantId), quantity: qty || 1 }], sections: SECTION_ID, sections_url: location.pathname });

  function addForm(form) {
    const fd = new FormData(form);
    fd.append('sections', SECTION_ID);
    fd.append('sections_url', location.pathname);
    return postCart('/cart/add.js', fd);
  }

  const changeKey = (key, qty) =>
    postCart('/cart/change.js', { id: key, quantity: qty, sections: SECTION_ID, sections_url: location.pathname });

  /* ── Delegated clicks ── */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-smores-cart-close]')) { e.preventDefault(); return close(); }
    if (e.target.closest('[data-cart-open], .smores-header__icon-btn--cart')) { e.preventDefault(); return open(); }

    const rem = e.target.closest('[data-line-remove]');
    if (rem) { e.preventDefault(); changeKey(rem.dataset.key, 0); return; }

    const minus = e.target.closest('[data-qty-minus]');
    if (minus) { e.preventDefault(); changeKey(minus.dataset.key, parseInt(minus.dataset.qty, 10)); return; }

    const plus = e.target.closest('[data-qty-plus]');
    if (plus) { e.preventDefault(); changeKey(plus.dataset.key, parseInt(plus.dataset.qty, 10)); return; }

    const up = e.target.closest('[data-upsell-add]');
    if (up) { e.preventDefault(); up.setAttribute('aria-busy', 'true'); addVariant(up.dataset.variant, 1).finally(() => up.removeAttribute('aria-busy')); return; }
  });

  /* ── Intercept every add-to-cart form ── */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form[action*="/cart/add"], form[action$="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    if (btn) btn.setAttribute('aria-busy', 'true');
    addForm(form)
      .then((data) => { if (!(data && data.status)) open(); })
      .finally(() => { if (btn) btn.removeAttribute('aria-busy'); });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && root.classList.contains('smores-cart-open')) close();
  });

  /* ── External triggers (e.g. bundle "Add all to cart") ── */
  document.addEventListener('smores:cart:refresh', () => { refresh().then(open); });

  window.SmoresCart = { open, close, refresh, addVariant };
})();
